import React, { useState, useEffect } from 'react';
import {
  Handshake, ExternalLink, Globe, Mail, Twitter, Linkedin, Instagram,
  ArrowRight, Sparkles, Star, ChevronRight, Users, Zap, Award, Shield,
  Building2, Heart, MessageCircle, Gamepad2
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_CONFIG = {
  platinum: { label: 'Platinum', color: '#E5E4E2', bg: 'bg-white/10', border: 'border-white/20', accent: 'from-white/20 to-white/5' },
  gold: { label: 'Gold', color: '#F59E0B', bg: 'bg-amber-500/10', border: 'border-amber-500/20', accent: 'from-amber-500/20 to-amber-500/5' },
  silver: { label: 'Silver', color: '#94A3B8', bg: 'bg-slate-400/10', border: 'border-slate-400/20', accent: 'from-slate-400/20 to-slate-400/5' },
};

const TYPE_CONFIG = {
  partner: { label: 'Partner', icon: Handshake, color: '#00E5FF' },
  affiliate: { label: 'Affiliate', icon: Users, color: '#8A2BE2' },
  sponsor: { label: 'Sponsor', icon: Award, color: '#F59E0B' },
};

const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [filter, setFilter] = useState('all');
  const [failedLogos, setFailedLogos] = useState({});

  useEffect(() => { fetchPartners(); }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/partners/public`);
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    if (path.startsWith('/api/uploads/')) return `${BACKEND_URL}${path}`;
    if (path.startsWith('/uploads/')) return `${BACKEND_URL}/api${path}`;
    return `${BACKEND_URL}${path}`;
  };

  const getTier = (tier) => TIER_CONFIG[tier] || TIER_CONFIG.silver;
  const getType = (type) => TYPE_CONFIG[type] || TYPE_CONFIG.partner;

  const filteredPartners = partners.filter(p => filter === 'all' || p.partner_type === filter);
  const featuredPartners = filteredPartners.filter(p => p.featured);
  const regularPartners = filteredPartners.filter(p => !p.featured);

  return (
    <div className="min-h-screen bg-[#05050A]" data-testid="affiliates-page">

      {/* Hero */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/3 w-[600px] h-[600px] bg-[#8A2BE2]/[0.03] rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#00E5FF]/[0.03] rounded-full blur-[120px]" />
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-md mb-8"
            style={{ animation: 'fadeInUp 0.6s ease-out' }}>
            <Handshake className="w-3.5 h-3.5 text-[#8A2BE2]" />
            <span className="text-xs tracking-[0.2em] uppercase text-white/50 font-medium">Our Network</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-white mb-6"
            style={{ animation: 'fadeInUp 0.6s ease-out 0.1s both' }}>
            Our{' '}
            <span className="bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] bg-clip-text text-transparent font-medium">
              Affiliates
            </span>
          </h1>

          <p className="text-base md:text-lg text-white/40 max-w-xl mx-auto leading-relaxed mb-10"
            style={{ animation: 'fadeInUp 0.6s ease-out 0.2s both' }}>
            We collaborate with industry leaders and innovative companies to deliver exceptional experiences.
          </p>

          <div className="flex justify-center gap-10 md:gap-16" style={{ animation: 'fadeInUp 0.6s ease-out 0.3s both' }}>
            {[
              { value: partners.length, label: 'Partners' },
              { value: featuredPartners.length || partners.filter(p => p.featured).length, label: 'Featured' },
              { value: new Set(partners.map(p => p.partner_type)).size, label: 'Categories' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-2xl md:text-3xl font-light bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] bg-clip-text text-transparent">{stat.value}</p>
                <p className="text-[10px] tracking-[0.15em] uppercase text-white/25 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 border-y border-white/[0.03]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="flex justify-center gap-1.5">
            {['all', 'partner', 'affiliate', 'sponsor'].map((type) => (
              <button key={type} onClick={() => setFilter(type)} data-testid={`filter-${type}`}
                className={`px-5 py-2.5 rounded-xl text-xs font-medium transition-all ${
                  filter === type
                    ? 'bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/25'
                    : 'bg-white/[0.02] text-white/30 border border-white/[0.04] hover:text-white/50 hover:border-white/10'
                }`}>
                {type === 'all' ? 'All' : `${(TYPE_CONFIG[type]?.label || type)}s`}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Partners */}
      {featuredPartners.length > 0 && (
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] mb-5">
                <Star className="w-3 h-3 text-[#F59E0B]" />
                <span className="text-[10px] tracking-[0.2em] uppercase text-white/40 font-medium">Featured</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-light tracking-tight text-white">
                Key <span className="text-[#00E5FF]">Partners</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              {featuredPartners.map((partner, index) => {
                const tier = getTier(partner.tier);
                const type = getType(partner.partner_type);
                const socialCount = Object.values(partner.social_links || {}).filter(Boolean).length + (partner.website_url ? 1 : 0);
                return (
                  <div key={partner.id} data-testid={`featured-partner-${partner.id}`}
                    onClick={() => setSelectedPartner(partner)}
                    className="group relative rounded-2xl border border-white/[0.06] bg-[#0A0A12] hover:border-[#00E5FF]/25 cursor-pointer transition-all duration-500 overflow-hidden hover:-translate-y-1"
                    style={{
                      animation: `fadeInUp 0.5s ease-out ${0.08 * index}s both`,
                      boxShadow: '0 0 0 rgba(0,229,255,0)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 0 40px rgba(0,229,255,0.08)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 0 0 rgba(0,229,255,0)'; }}>
                    {/* Ambient gradient orb */}
                    <div className="pointer-events-none absolute -top-16 -right-16 w-40 h-40 bg-[#00E5FF]/[0.05] rounded-full blur-[60px] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="pointer-events-none absolute -bottom-12 -left-12 w-32 h-32 bg-[#8A2BE2]/[0.05] rounded-full blur-[50px] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                    {/* Featured jewel corner */}
                    <div className="absolute top-3 right-3 w-7 h-7 rounded-lg bg-white/[0.03] backdrop-blur-md border border-white/10 flex items-center justify-center" title="Featured">
                      <Star className="w-3 h-3 text-amber-300 fill-amber-300" strokeWidth={0} />
                    </div>

                    <div className="relative p-6">
                      <div className="flex items-start gap-4">
                        {/* Luxury logo container with glow */}
                        <div className="relative flex-shrink-0">
                          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-white/[0.04] to-[#0A0A12] border border-white/[0.08] p-2.5 flex items-center justify-center overflow-hidden group-hover:scale-105 transition-transform duration-500"
                            style={{ boxShadow: 'inset 0 0 0 1px rgba(0,229,255,0.06)' }}>
                            {partner.logo_url && !failedLogos[partner.id] ? (
                              <img src={getImageUrl(partner.logo_url)} alt={partner.name} className="max-w-full max-h-full object-contain"
                                onError={() => setFailedLogos(prev => ({ ...prev, [partner.id]: true }))} />
                            ) : (
                              <Handshake className="w-7 h-7 text-[#00E5FF]/70" />
                            )}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-medium text-white group-hover:text-[#00E5FF] transition-colors mb-1.5 tracking-tight truncate">{partner.name}</h3>
                          <div className="flex gap-1.5 flex-wrap mb-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] tracking-[0.08em] font-medium border ${tier.bg} ${tier.border}`}
                              style={{ color: tier.color }}>
                              <Star className="w-2.5 h-2.5" />{tier.label}
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] tracking-[0.08em] font-medium border bg-white/[0.02] border-white/[0.08] text-white/50"
                              style={{ color: type.color }}>
                              <type.icon className="w-2.5 h-2.5" />{type.label}
                            </span>
                          </div>
                          <p className="text-white/40 text-xs leading-relaxed line-clamp-2">{partner.description}</p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/[0.04]">
                        <div className="flex gap-1.5">
                          <SocialIconStrip partner={partner} />
                        </div>
                        <span className="text-white/25 text-[10px] tracking-[0.12em] uppercase flex items-center group-hover:text-[#00E5FF] transition-colors font-medium">
                          View Details <ChevronRight className="w-3.5 h-3.5 ml-0.5 group-hover:translate-x-0.5 transition-transform" />
                        </span>
                      </div>
                    </div>

                    {/* Bottom accent line on hover */}
                    <div className="absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* All Partners Grid */}
      {regularPartners.length > 0 && (
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            {featuredPartners.length > 0 && (
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] mb-5">
                  <Users className="w-3 h-3 text-[#8A2BE2]" />
                  <span className="text-[10px] tracking-[0.2em] uppercase text-white/40 font-medium">Network</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-light tracking-tight text-white">
                  All <span className="text-[#8A2BE2]">Affiliates</span>
                </h2>
              </div>
            )}

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {regularPartners.map((partner, index) => {
                const type = getType(partner.partner_type);
                return (
                  <div key={partner.id} data-testid={`partner-card-${partner.id}`}
                    onClick={() => setSelectedPartner(partner)}
                    className="group relative rounded-2xl border border-white/[0.06] bg-[#0A0A12] hover:border-[#8A2BE2]/25 cursor-pointer transition-all duration-500 overflow-hidden hover:-translate-y-1 p-5"
                    style={{
                      animation: `fadeInUp 0.45s ease-out ${0.05 * index}s both`,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 0 30px rgba(138,43,226,0.06)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-white/[0.04] to-[#0A0A12] border border-white/[0.08] p-2 flex items-center justify-center overflow-hidden flex-shrink-0 group-hover:scale-105 transition-transform duration-500"
                        style={{ boxShadow: 'inset 0 0 0 1px rgba(138,43,226,0.05)' }}>
                        {partner.logo_url && !failedLogos[`grid-${partner.id}`] ? (
                          <img src={getImageUrl(partner.logo_url)} alt={partner.name} className="max-w-full max-h-full object-contain"
                            onError={() => setFailedLogos(prev => ({ ...prev, [`grid-${partner.id}`]: true }))} />
                        ) : (
                          <Handshake className="w-5 h-5 text-[#00E5FF]/60" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-medium text-white truncate group-hover:text-[#00E5FF] transition-colors tracking-tight">{partner.name}</h3>
                        <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[9px] tracking-[0.08em] uppercase font-medium border bg-white/[0.02] border-white/[0.06] text-white/40"
                          style={{ color: type.color }}>
                          <type.icon className="w-2.5 h-2.5" />{type.label}
                        </span>
                      </div>
                    </div>
                    <p className="text-white/30 text-xs line-clamp-2 leading-relaxed mb-3 min-h-[32px]">{partner.description}</p>
                    <div className="flex items-center justify-between pt-3 border-t border-white/[0.04]">
                      <SocialIconStrip partner={partner} compact />
                      <ChevronRight className="w-4 h-4 text-white/15 group-hover:text-[#8A2BE2] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                    </div>

                    {/* Bottom accent line on hover */}
                    <div className="absolute bottom-0 left-5 right-5 h-px bg-gradient-to-r from-transparent via-[#8A2BE2]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Empty State */}
      {partners.length === 0 && !loading && (
        <section className="py-24">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <div className="w-20 h-20 bg-white/[0.02] border border-white/[0.06] rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Handshake className="w-10 h-10 text-white/10" />
            </div>
            <h3 className="text-xl font-medium text-white mb-2">Coming Soon</h3>
            <p className="text-white/25 text-sm">We are building our affiliate network. Check back soon.</p>
          </div>
        </section>
      )}

      {loading && (
        <div className="flex justify-center py-24">
          <div className="w-8 h-8 rounded-full border border-white/10 border-t-[#00E5FF] animate-spin" />
        </div>
      )}

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="relative rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden" data-testid="partner-cta">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-[#8A2BE2]/[0.04] rounded-full blur-[80px]" />
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-48 h-24 bg-[#00E5FF]/[0.04] rounded-full blur-[60px]" />
            <div className="relative p-12 md:p-16 text-center">
              <div className="w-14 h-14 bg-gradient-to-br from-[#8A2BE2]/10 to-[#00E5FF]/10 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-white/[0.06]">
                <Handshake className="w-6 h-6 text-[#8A2BE2]" />
              </div>
              <h2 className="text-xl md:text-2xl font-light tracking-tight text-white mb-3">
                Interested in{' '}
                <span className="bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] bg-clip-text text-transparent">Partnering</span>?
              </h2>
              <p className="text-sm text-white/30 mb-8 max-w-md mx-auto">
                Join our network and create exceptional experiences together.
              </p>
              <a href="mailto:partnerships@starnightholdings.uk" data-testid="partnership-email-btn"
                className="inline-flex items-center gap-2 bg-white text-black font-medium text-sm px-7 py-3 rounded-lg hover:bg-white/90 transition-all group">
                <Mail className="w-4 h-4" />
                Contact Us
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <p className="text-white/15 text-xs mt-5">partnerships@starnightholdings.uk</p>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Detail Modal */}
      <Dialog open={!!selectedPartner} onOpenChange={() => setSelectedPartner(null)}>
        <DialogContent className="bg-[#0A0A12] border border-white/[0.08] text-white max-w-xl shadow-2xl shadow-black/60 p-0 overflow-hidden">
          {selectedPartner && (
            <>
              {/* Modal Header with gradient banner */}
              <div className={`relative h-28 bg-gradient-to-br ${getTier(selectedPartner.tier).accent} overflow-hidden`}>
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0A0A12]" />
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                {/* Tier ribbon */}
                <div className="absolute top-4 right-4">
                  <Badge className={`${getTier(selectedPartner.tier).bg} ${getTier(selectedPartner.tier).border} text-[10px] backdrop-blur-sm px-2.5 py-1`}
                    style={{ color: getTier(selectedPartner.tier).color }}>
                    <Star className="w-2.5 h-2.5 mr-1" />{getTier(selectedPartner.tier).label}
                  </Badge>
                </div>
              </div>

              {/* Logo overlapping banner */}
              <div className="px-6 -mt-10 relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-[#0D0D15] border-2 border-white/[0.08] p-2 flex items-center justify-center overflow-hidden shadow-xl">
                  {selectedPartner.logo_url && !failedLogos[`detail-${selectedPartner.id}`] ? (
                    <img src={getImageUrl(selectedPartner.logo_url)} alt={selectedPartner.name} className="max-w-full max-h-full object-contain"
                      onError={() => setFailedLogos(prev => ({ ...prev, [`detail-${selectedPartner.id}`]: true }))} />
                  ) : (
                    <Handshake className="w-7 h-7 text-[#00E5FF]" />
                  )}
                </div>
              </div>

              <div className="px-6 pb-6 pt-3 space-y-5">
                {/* Name and badges */}
                <div>
                  <h2 className="text-xl font-semibold text-white tracking-tight">{selectedPartner.name}</h2>
                  <div className="flex gap-1.5 mt-2">
                    <Badge className={`${getTier(selectedPartner.tier).bg} ${getTier(selectedPartner.tier).border} text-[10px]`}
                      style={{ color: getTier(selectedPartner.tier).color }}>
                      {getTier(selectedPartner.tier).label}
                    </Badge>
                    <Badge className="bg-white/[0.03] border-white/[0.06] text-white/40 text-[10px]">
                      {getType(selectedPartner.partner_type).label}
                    </Badge>
                    {selectedPartner.established_year && (
                      <Badge className="bg-white/[0.03] border-white/[0.06] text-white/30 text-[10px]">
                        Est. {selectedPartner.established_year}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* About */}
                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                  <h3 className="text-[10px] font-semibold text-[#00E5FF] mb-2.5 uppercase tracking-[0.15em]">About</h3>
                  <p className="text-white/40 text-sm leading-relaxed">{selectedPartner.detailed_description || selectedPartner.description}</p>
                </div>

                {/* Benefits */}
                {selectedPartner.benefits?.length > 0 && (
                  <div className="p-4 rounded-xl bg-gradient-to-br from-[#00E5FF]/[0.03] to-transparent border border-[#00E5FF]/10">
                    <h3 className="text-[10px] font-semibold text-[#00E5FF] mb-3 uppercase tracking-[0.15em] flex items-center gap-1.5">
                      <Zap className="w-3 h-3" /> Benefits
                    </h3>
                    <div className="space-y-2">
                      {selectedPartner.benefits.map((b, i) => (
                        <div key={i} className="flex items-start gap-2.5">
                          <div className="w-5 h-5 rounded-md bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Shield className="w-2.5 h-2.5 text-[#00E5FF]" />
                          </div>
                          <span className="text-white/50 text-sm">{b}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Social links */}
                <div className="flex flex-wrap gap-2 pt-4 border-t border-white/[0.04]">
                  {selectedPartner.website_url && (
                    <a href={selectedPartner.website_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-[#00E5FF]/10 hover:border-[#00E5FF]/20 text-white/50 hover:text-[#00E5FF] text-xs font-medium transition-all">
                      <Globe className="w-3.5 h-3.5" /> Website <ExternalLink className="w-3 h-3 opacity-50" />
                    </a>
                  )}
                  {selectedPartner.social_links?.discord && (
                    <a href={selectedPartner.social_links.discord} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#5865F2]/10 border border-[#5865F2]/20 text-[#5865F2] text-xs font-medium hover:bg-[#5865F2]/20 transition-all">
                      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current"><path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03z"/></svg>
                      Discord
                    </a>
                  )}
                  {selectedPartner.social_links?.twitter && (
                    <a href={selectedPartner.social_links.twitter} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-[#00E5FF]/10 hover:border-[#00E5FF]/20 text-white/50 hover:text-[#00E5FF] text-xs font-medium transition-all">
                      <Twitter className="w-3.5 h-3.5" /> Twitter
                    </a>
                  )}
                  {selectedPartner.social_links?.instagram && (
                    <a href={selectedPartner.social_links.instagram} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-[#00E5FF]/10 hover:border-[#00E5FF]/20 text-white/50 hover:text-[#00E5FF] text-xs font-medium transition-all">
                      <Instagram className="w-3.5 h-3.5" /> Instagram
                    </a>
                  )}
                  {selectedPartner.social_links?.roblox_group && (
                    <a href={selectedPartner.social_links.roblox_group} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs font-medium hover:bg-emerald-500/20 transition-all">
                      <Gamepad2 className="w-3.5 h-3.5" /> Roblox Group <ExternalLink className="w-3 h-3 opacity-50" />
                    </a>
                  )}
                  {selectedPartner.social_links?.roblox_discord && (
                    <a href={selectedPartner.social_links.roblox_discord} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#5865F2]/10 border border-[#5865F2]/25 text-[#8ea1ff] text-xs font-medium hover:bg-[#5865F2]/20 transition-all">
                      <Gamepad2 className="w-3.5 h-3.5" /> Roblox Community <MessageCircle className="w-3 h-3 opacity-70" />
                    </a>
                  )}
                  {selectedPartner.social_links?.linkedin && (
                    <a href={selectedPartner.social_links.linkedin} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-[#00E5FF]/10 hover:border-[#00E5FF]/20 text-white/50 hover:text-[#00E5FF] text-xs font-medium transition-all">
                      <Linkedin className="w-3.5 h-3.5" /> LinkedIn
                    </a>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <div className="h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" />
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

// Reusable social icon strip (used on featured + grid cards)
const SocialIconStrip = ({ partner, compact = false }) => {
  const size = compact ? 'p-1.5' : 'p-2';
  const icon = compact ? 'w-3 h-3' : 'w-3.5 h-3.5';
  return (
    <div className="flex gap-1.5 flex-wrap">
      {partner.website_url && (
        <a href={partner.website_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title="Website"
          className={`${size} rounded-lg bg-white/[0.03] hover:bg-[#00E5FF]/10 border border-white/[0.04] hover:border-[#00E5FF]/20 transition-all group/link`}>
          <Globe className={`${icon} text-white/40 group-hover/link:text-[#00E5FF]`} />
        </a>
      )}
      {partner.social_links?.discord && (
        <a href={partner.social_links.discord} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title="Discord"
          className={`${size} rounded-lg bg-[#5865F2]/5 hover:bg-[#5865F2]/15 border border-[#5865F2]/10 hover:border-[#5865F2]/30 transition-all`}>
          <svg viewBox="0 0 24 24" className={`${icon} text-[#8ea1ff]/70 hover:text-[#8ea1ff] fill-current`}><path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03z"/></svg>
        </a>
      )}
      {partner.social_links?.roblox_group && (
        <a href={partner.social_links.roblox_group} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title="Roblox Group"
          className={`${size} rounded-lg bg-emerald-500/[0.06] hover:bg-emerald-500/15 border border-emerald-500/15 hover:border-emerald-500/30 transition-all`}>
          <Gamepad2 className={`${icon} text-emerald-300/70 hover:text-emerald-300`} />
        </a>
      )}
      {partner.social_links?.roblox_discord && (
        <a href={partner.social_links.roblox_discord} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title="Roblox Community Discord"
          className={`${size} rounded-lg bg-[#5865F2]/[0.06] hover:bg-[#5865F2]/15 border border-[#5865F2]/15 hover:border-[#5865F2]/30 transition-all relative`}>
          <Gamepad2 className={`${icon} text-[#8ea1ff]/70 hover:text-[#8ea1ff]`} />
        </a>
      )}
      {partner.social_links?.twitter && (
        <a href={partner.social_links.twitter} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title="Twitter / X"
          className={`${size} rounded-lg bg-white/[0.03] hover:bg-[#00E5FF]/10 border border-white/[0.04] hover:border-[#00E5FF]/20 transition-all group/link`}>
          <Twitter className={`${icon} text-white/40 group-hover/link:text-[#00E5FF]`} />
        </a>
      )}
      {partner.social_links?.instagram && (
        <a href={partner.social_links.instagram} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title="Instagram"
          className={`${size} rounded-lg bg-white/[0.03] hover:bg-[#00E5FF]/10 border border-white/[0.04] hover:border-[#00E5FF]/20 transition-all group/link`}>
          <Instagram className={`${icon} text-white/40 group-hover/link:text-[#00E5FF]`} />
        </a>
      )}
    </div>
  );
};

export default Partners;
